import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  Badge,
  Banner,
  ButtonGroup,
  Card,
  CardBody,
  CardFoot,
  CardHead,
  KV,
  KVRow,
  Ladder,
  LoadError,
  OutcomeBadge,
  PageHead,
  Seg,
  SideBadge,
  Skeleton,
  StateBlock,
  VetoLine,
} from '../components/ui.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { useApi } from '../lib/api.ts'
import { ago, clock, gbp, odds, shortDate } from '../lib/format.ts'
import type { DecisionDetail, DecisionsPayload } from '../../shared/types.ts'

type OutcomeFilter = 'all' | 'VETOED' | 'PLACED' | 'FAILED'
type Window = 'all' | 'today' | 'week'

/**
 * The audit trail: every decision each strategy made, whether or not it became an order.
 *
 * A veto is the risk gate doing its job, so it is presented as an explained limit — named
 * limit, threshold, observed value — never as an error.
 */
export function DecisionLog() {
  usePageTitle('Decision log')
  const [outcome, setOutcome] = useState<OutcomeFilter>('all')
  const [window, setWindow] = useState<Window>('today')
  const [strategy, setStrategy] = useState('all')
  const [expanded, setExpanded] = useState<number | null>(null)

  const path = `/decisions?outcome=${outcome}&window=${window}&strategy=${strategy}`
  const { data, error, loading, reload } = useApi<DecisionsPayload>(path, 8000)

  if (error) return <main className="content"><LoadError error={error} onRetry={reload} /></main>

  const counts = data?.counts

  return (
    <main className="content">
      <PageHead
        title="Decision log"
        sub="Every decision each strategy made — whether or not it became an order. This is the audit trail: what was seen, what was intended, and why it did or didn't trade."
      />

      <Banner tone="incident" title="A veto is the risk gate doing its job">
        When a decision is marked <Badge tone="vetoed">VETOED</Badge> the strategy wanted to trade but a named limit
        stopped it. Every veto shows the limit, its threshold and the value that would have breached it. Vetoes are
        expected — a day with zero vetoes usually means the limits are set too loose.
      </Banner>

      <Card style={{ marginBottom: 16 }}>
        <CardBody style={{ padding: '14px 18px' }}>
          <div className="row wrap" style={{ gap: 14 }}>
            <ButtonGroup
              label="Outcome filter"
              value={outcome}
              onChange={setOutcome}
              options={[
                { value: 'all', label: 'All outcomes' },
                { value: 'VETOED', label: 'Only vetoes' },
                { value: 'PLACED', label: 'Only placed' },
                { value: 'FAILED', label: 'Only failed' },
              ]}
            />
            <Seg
              label="Time window"
              value={window}
              onChange={setWindow}
              options={[
                { value: 'all', label: 'All time' },
                { value: 'today', label: 'Only today' },
                { value: 'week', label: 'This week' },
              ]}
            />
            <label className="lbl u-sm" style={{ marginLeft: 'auto' }} htmlFor="dl-strategy">
              Strategy
            </label>
            <select
              id="dl-strategy"
              className="select"
              style={{ width: 'auto', minWidth: 200 }}
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
            >
              <option value="all">All strategies</option>
              <option value="goal_drift">Only this strategy · Goal drift</option>
              <option value="mean_reversion">Only this strategy · Mean reversion</option>
              <option value="manual">Only this strategy · Manual</option>
            </select>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead
          title={window === 'today' ? `Today · ${shortDate(new Date().toISOString())}` : window === 'week' ? 'This week' : 'All time'}
          sub={
            counts
              ? `${counts.total} decisions · ${counts.skipped} skipped · ${counts.placed} placed · ${counts.vetoed} vetoed · ${counts.failed} failed`
              : undefined
          }
          right={
            <span className="freshness live">
              <span className="fr-dot" />
              Streaming · last decision {ago(data?.lastDecisionAgoS)}
            </span>
          }
        />
        <CardBody flush>
          {loading ? (
            <div style={{ padding: 18 }}>
              <Skeleton rows={8} />
            </div>
          ) : data && data.rows.length === 0 ? (
            <StateBlock
              icon="◇"
              title="No decisions logged for this filter"
              text="Both strategies are enabled and inside their evaluation loop. The first decision will appear here the moment a strategy evaluates a qualifying market."
              action={
                <Link className="btn outline sm" to="/strategies">
                  Check strategy status
                </Link>
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Strategy</th>
                    <th>Market</th>
                    <th>Runner</th>
                    <th>Action</th>
                    <th>Side</th>
                    <th className="num">Price</th>
                    <th className="num">Size</th>
                    <th>Outcome</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data?.rows.map((d) => (
                    <Fragment key={d.id}>
                      <tr>
                        <td className="u-mono">{clock(d.ts)}</td>
                        <td>{d.strategy}</td>
                        <td>
                          <Link className="cell-strong" to={`/markets/${d.marketId}`}>
                            {d.event}
                          </Link>
                          <div className="cell-sub">{d.marketType}</div>
                        </td>
                        <td>{d.runner ?? <span className="muted">—</span>}</td>
                        <td>{d.action === 'NO_ACTION' ? <span className="muted">—</span> : titleCase(d.action)}</td>
                        <td>{d.side ? <SideBadge side={d.side} /> : <span className="muted">—</span>}</td>
                        <td className="num">{odds(d.price)}</td>
                        <td className="num">{d.size === null ? <span className="muted">—</span> : gbp(d.size)}</td>
                        <td>
                          <OutcomeBadge outcome={d.outcome} />
                          <VetoLine text={d.detail} />
                          {/* A lay's liability, never its stake, is the number that matters. */}
                          {d.outcome === 'PLACED' && d.side === 'LAY' && d.liability !== null && (
                            <div className="veto-line">liability {gbp(d.liability)} at {odds(d.price)}</div>
                          )}
                        </td>
                        <td className="num">
                          <button
                            className="btn ghost sm"
                            aria-expanded={expanded === d.id}
                            onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                          >
                            {expanded === d.id ? 'Hide' : 'Details'}
                          </button>
                        </td>
                      </tr>
                      {expanded === d.id && (
                        <tr>
                          <td colSpan={10} style={{ background: 'var(--bg-2)', padding: 0 }}>
                            <DecisionExpander id={d.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
        <CardFoot>
          Showing {data?.shown ?? 0} of {counts?.total ?? 0} decisions for the current filter. Filtering runs
          server-side across every row, not just the page you can see.
        </CardFoot>
      </Card>

      {/* Enabled but silent is a normal, explained state — never a blank. */}
      {data && data.silentStrategies.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <CardHead
            title="A strategy producing nothing"
            sub="Enabled, but silent — always explained, never a blank"
          />
          <CardBody flush>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Strategy</th>
                    <th>State</th>
                    <th>Why no decisions</th>
                    <th className="num">Last decision</th>
                  </tr>
                </thead>
                <tbody>
                  {data.silentStrategies.map((s) => (
                    <tr key={s.key}>
                      <td className="cell-strong">{s.name}</td>
                      <td>
                        <Badge tone={s.status === 'throttled' ? 'warn' : s.status === 'disabled' ? 'skipped' : undefined} dot>
                          {titleCase(s.status.replace(/_/g, ' '))}
                        </Badge>
                      </td>
                      <td>{s.why}</td>
                      <td className="num">
                        {s.lastDecisionAt ? clock(s.lastDecisionAt) : <span className="muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
          <CardFoot>
            "Enabled but silent" is a normal, explained state — a strategy with nothing to model is not broken.
          </CardFoot>
        </Card>
      )}

      <p className="u-faint u-sm" style={{ marginTop: 24 }}>
        Paper mode · simulated fills on real market data · no money at risk.
      </p>
    </main>
  )
}

/** What was seen, what was intended, and which gates it passed. */
function DecisionExpander({ id }: { id: number }) {
  const { data, error, loading } = useApi<DecisionDetail>(`/decisions/${id}`)

  if (loading) {
    return (
      <div style={{ padding: '18px 20px' }}>
        <Skeleton rows={3} />
      </div>
    )
  }
  if (error || !data) {
    return <div style={{ padding: '18px 20px' }} className="u-faint">Could not load this decision: {error}</div>
  }

  return (
    <div style={{ padding: '18px 20px' }}>
      <div className="between" style={{ marginBottom: 14 }}>
        <div className="cell-strong">
          Decision d-{data.id} · {data.strategy} · {data.event} · {data.runner ?? '—'}
          {data.price ? ` @ ${odds(data.price)}` : ''}
        </div>
        <OutcomeBadge outcome={data.outcome} />
      </div>

      <div className="grid cols-3">
        <div>
          <div className="lbl" style={{ marginBottom: 8 }}>
            Signal that produced the decision
          </div>
          {data.signal ? (
            <KV>
              {Object.entries(data.signal).map(([k, v]) => (
                <KVRow key={k} label={titleCase(k.replace(/_/g, ' '))} text={typeof v === 'string'}>
                  {formatSignal(v)}
                </KVRow>
              ))}
            </KV>
          ) : (
            <div className="u-faint u-sm">
              No signal snapshot was stored with this decision. Without it the decision cannot be reconstructed from
              this screen alone.
            </div>
          )}
        </div>

        <div>
          <div className="lbl" style={{ marginBottom: 8 }}>
            Order-book snapshot at decision
          </div>
          {data.book ? (
            <>
              <Ladder backs={data.book.backLadder} lays={data.book.layLadder} />
              <div className="cell-sub" style={{ marginTop: 8 }}>
                {data.book.runner} · best back {odds(data.book.backLadder[0]?.price ?? null)} · best lay{' '}
                {odds(data.book.layLadder.at(-1)?.price ?? null)}
              </div>
            </>
          ) : (
            <div className="u-faint u-sm">No book snapshot stored for this decision.</div>
          )}
        </div>

        <div>
          <div className="lbl" style={{ marginBottom: 8 }}>
            Risk gate checks
          </div>
          {data.gates.length === 0 ? (
            <div className="u-faint u-sm">This decision never reached the gate — the strategy chose not to act.</div>
          ) : (
            <KV>
              {data.gates.map((g) => (
                <KVRow key={g.label} label={titleCase(g.label)} text>
                  <span className={`badge ${g.passed ? 'pos' : 'vetoed'}`}>{g.detail}</span>
                </KVRow>
              ))}
            </KV>
          )}
          <div className="cell-sub" style={{ marginTop: 8 }}>
            {data.routedNote}
          </div>
        </div>
      </div>
    </div>
  )
}

function formatSignal(v: unknown): string {
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(3)
  if (typeof v === 'boolean') return v ? 'yes' : 'no'
  return String(v)
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}
